import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Send, MessageSquare, User, Calendar, Clock, Radio } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { TopNav } from "@/components/TopNav";
import { LiveBadge } from "@/components/LiveBadge";
import { VideoPlayer } from "@/components/VideoPlayer";
import { fmtDateTime, fromNow } from "@/lib/time";

export default function WebinarWatch() {
  const { id } = useParams();
  const nav = useNavigate();
  const [webinar, setWebinar] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [text, setText] = useState("");
  const [sending, setSending] = useState(false);
  const listRef = useRef(null);

  const loadWebinar = () => api.get(`/webinars/${id}`).then((r) => setWebinar(r.data)).catch(() => nav("/"));
  const loadQuestions = () => api.get(`/webinars/${id}/questions`).then((r) => setQuestions(r.data));

  useEffect(() => {
    loadWebinar();
    loadQuestions();
    const t = setInterval(() => { loadWebinar(); loadQuestions(); }, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [id]);

  const submit = async (e) => {
    e.preventDefault();
    if (!text.trim()) return;
    setSending(true);
    try {
      const { data } = await api.post(`/webinars/${id}/questions`, { question_text: text });
      setQuestions((q) => [...q, data]);
      setText("");
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally {
      setSending(false);
    }
  };

  if (!webinar) return <div className="min-h-screen bg-[#FFFFFF]" />;
  const isLive = webinar.status === "live";

  return (
    <div className="min-h-screen bg-[#FFFFFF] text-[#0A0A0A]">
      <TopNav />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <button onClick={() => nav("/")} data-testid="back-btn" className="flex items-center gap-2 text-sm text-gray-400 hover:text-[#0A0A0A] mb-5 transition-colors">
          <ArrowLeft size={16} /> Back to webinars
        </button>

        <div className="grid lg:grid-cols-12 gap-6">
          {/* Player 70% */}
          <div className="lg:col-span-8">
            {isLive ? (
              <VideoPlayer src={webinar.playback_url} />
            ) : (
              <div className="w-full aspect-video bg-[#F5F5F5] border border-black/10 rounded-sm flex flex-col items-center justify-center gap-3 text-center px-6">
                <Radio className="text-gray-600" size={40} strokeWidth={1.5} />
                {webinar.status === "upcoming" ? (
                  <>
                    <p className="font-display font-bold text-xl">Webinar starts soon</p>
                    <p className="text-gray-500 text-sm">Scheduled for {fmtDateTime(webinar.start_datetime)}</p>
                  </>
                ) : (
                  <>
                    <p className="font-display font-bold text-xl">This webinar has ended</p>
                    <p className="text-gray-500 text-sm">Recordings are not available.</p>
                  </>
                )}
              </div>
            )}

            <div className="mt-5">
              <div className="flex items-center gap-3 mb-2">
                {isLive && <LiveBadge />}
                <h1 className="font-display font-black text-2xl sm:text-3xl tracking-tight">{webinar.title}</h1>
              </div>
              <p className="text-gray-400 leading-relaxed mb-4">{webinar.description}</p>
              <div className="flex flex-wrap gap-5 text-sm text-gray-500">
                <span className="flex items-center gap-2"><User size={15} /> {webinar.presenter_name || "TBA"}</span>
                <span className="flex items-center gap-2"><Calendar size={15} /> {fmtDateTime(webinar.start_datetime)}</span>
                <span className="flex items-center gap-2"><Clock size={15} /> {webinar.duration_minutes} min</span>
              </div>
            </div>
          </div>

          {/* Q&A 30% */}
          <div className="lg:col-span-4">
            <div className="bg-[#F5F5F5] border border-[#E4E4E7] rounded-sm flex flex-col h-[70vh] lg:h-full lg:min-h-[560px]">
              <div className="flex items-center gap-2 px-5 py-4 border-b border-[#E4E4E7]">
                <MessageSquare className="text-[#0090A8]" size={18} />
                <h2 className="font-display font-bold uppercase tracking-wide text-sm">Live Q&A</h2>
              </div>

              <div ref={listRef} className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="qa-list">
                {questions.length === 0 ? (
                  <div className="h-full flex flex-col items-center justify-center text-center text-gray-600 px-4">
                    <MessageSquare size={28} className="mb-3 text-gray-700" />
                    <p className="text-sm">Your questions will appear here. Ask the presenter anything!</p>
                  </div>
                ) : (
                  questions.map((q) => (
                    <div key={q.id} data-testid={`my-question-${q.id}`} className="bg-[#FFFFFF] border border-[#E4E4E7] rounded-sm p-3">
                      <p className="text-sm text-gray-700 mb-2">{q.question_text}</p>
                      <div className="flex items-center justify-between">
                        <span className="text-[0.65rem] text-gray-600">{fromNow(q.created_at)}</span>
                        <span className={`text-[0.6rem] font-bold uppercase tracking-wider px-2 py-0.5 rounded-sm border ${
                          q.status === "answered" ? "text-[#12805C] border-[#12805C]/30 bg-[#12805C]/10" : "text-[#0090A8] border-[#0090A8]/30 bg-[#0090A8]/10"
                        }`}>
                          {q.status === "answered" ? "Answered" : "Submitted"}
                        </span>
                      </div>
                    </div>
                  ))
                )}
              </div>

              <form onSubmit={submit} className="p-4 border-t border-[#E4E4E7]">
                <div className="flex gap-2">
                  <input
                    data-testid="qa-input"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    placeholder={isLive ? "Type your question…" : "Q&A opens when live"}
                    disabled={!isLive}
                    className="flex-1 bg-[#FFFFFF] border border-[#E4E4E7] focus:border-[#0090A8] rounded-sm px-3 py-2.5 text-sm text-[#0A0A0A] placeholder-gray-400 outline-none transition-colors disabled:opacity-50"
                  />
                  <button
                    data-testid="qa-submit"
                    disabled={!isLive || sending}
                    className="bg-[#0090A8] text-black hover:bg-[#00728A] rounded-sm px-4 transition-colors disabled:opacity-40"
                  >
                    <Send size={16} />
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
